import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlContainer, FormGroup } from '@angular/forms';
import { SinglePackagingModel } from '../../models/single-packaging.model';
import { LanguageService } from '@hazmatsuite-next/ui';
import { Observable, debounceTime, map, take } from 'rxjs';
import { PackagingType } from '../../models/packaging-type.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EasyDgdService } from '../../services/easydgd.service';

@UntilDestroy()
@Component({
  selector: 'lib-easydgd-single-package-form',
  templateUrl: './single-package-form.component.html',
  styleUrls: ['./single-package-form.component.scss'],

})
export class SinglePackageFormComponent implements OnInit, OnChanges {
  @Input() set singlePackagingModel(m: SinglePackagingModel) {
    this.model = m;
  };

  @Input() packageTypes: PackagingType[] = [];
  @Input() packagingUnCodes: PackagingType[] = [];
  @Input() defaultPackageType = '';

  @Output() formValueChange = new EventEmitter<SinglePackagingModel>;

  public singlePackagingResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['SinglePackagingResource']));

  public validationResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['ValidationResource']));

  public model?: SinglePackagingModel;

  public form!: FormGroup;

  constructor(public controlContainer: ControlContainer, private languageService: LanguageService, private easyDgdService: EasyDgdService) {
    this.languageService.fetchResources(['SinglePackagingResource', 'ValidationResource']);
  }

  ngOnInit(): void {
    this.form = this.controlContainer.control as FormGroup;
    this.reInitform();

    this.technicalName?.valueChanges.pipe(
      debounceTime(200),
      untilDestroyed(this)
    ).subscribe(() => {
      this.easyDgdService.getProperShippingName(this.getModel()).pipe(take(1)).subscribe((result) => {
        this.properShippingName?.setValue(result, { onlySelf: true });
      });
    });

    this.alternativeProperShippingName?.valueChanges.pipe(
      debounceTime(200),
      untilDestroyed(this)
    ).subscribe(() => {
      this.easyDgdService.getProperShippingName(this.getModel()).pipe(take(1)).subscribe((result) => {
        this.properShippingName?.setValue(result, { onlySelf: true });
      });
    });

    this.useAlternativeProperShippingName?.valueChanges.pipe(
      debounceTime(200),
      untilDestroyed(this)
    ).subscribe(() => {
      this.easyDgdService.getProperShippingName(this.getModel()).pipe(take(1)).subscribe((result) => {
        this.properShippingName?.setValue(result, { onlySelf: true });
      });
    });

    this.form.valueChanges.pipe(debounceTime(100)).subscribe(() => {
      this.publishFormChange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const unCodesChanges = changes['packagingUnCodes'];
    if (unCodesChanges && unCodesChanges.currentValue !== unCodesChanges.previousValue) {
      setTimeout(() => {
        this.packagingUnCode?.setValue(this.packagingUnCode.value, { onlySelf: true });
      }, 100);
    }
  }

  publishFormChange() {
    this.formValueChange.next(this.getModel());
  }

  public changeUseAlternativeProperShippingName(evt: any): void {
    const checked = evt.currentTarget.checked;
    this.useAlternativeProperShippingName?.setValue(checked, { onlySelf: true });
    this.properShippingName?.enable();
  }

  public changeUseAlternativePackagingType(evt: any): void {
    const checked = evt.currentTarget.checked;
    this.useAlternativePackagingType?.setValue(checked);
  }

  public changeIsEnvironmentallyHazardous(evt: any): void {
    this.isEnvironmentallyHazardous?.setValue(evt.currentTarget.checked, { onlySelf: true });
  }

  public changePackagingType(evt: any): void {
    this.packagingType?.setValue(evt.currentTarget.value, { onlySelf: true });
  }

  public changePackagingUnCode(evt: any): void {
    // this.packagingUnCode?.setValue(evt.currentTarget.value, { onlySelf: true });
    this.publishFormChange();
  }

  private getModel(): SinglePackagingModel {
    return {
      ...this.model,
      UseAlternativeProperShippingName: this.useAlternativeProperShippingName?.value,
      UseAlternativePackagingType: this.useAlternativePackagingType?.value,
      ProperShippingName: this.properShippingName?.value,
      AlternativeProperShippingName: this.alternativeProperShippingName?.value,
      AlternativePackagingType: this.alternativePackagingType?.value,
      IsTechnicalNameRequired: this.model?.IsTechnicalNameRequired ?? false,
      TechnicalName: this.technicalName?.value,
      IsEnvironmentallyHazardous: this.isEnvironmentallyHazardous?.value,
      LowerFlashpoint: this.lowerFlashpoint?.value,
      PackagingType: this.packagingType?.value,
      PackagingUnCode: this.packagingUnCode?.value,
      NetWeightValue: this.netWeight?.value,
      NetWeightUnitSymbol: this.model?.NetWeightUnitSymbol ?? '',
      NetVolumeValue: this.netVolume?.value,
      NetVolumeUnitSymbol: this.model?.NetVolumeUnitSymbol ?? '',
      GrossWeightValue: this.grossWeight?.value,
      GrossWeightUnitSymbol: this.model?.GrossWeightUnitSymbol ?? '',
      NetExplosiveMassValue: this.netExplosiveMass?.value,
      NetExplosiveMassUnitSymbol: this.model?.NetExplosiveMassUnitSymbol ?? '',
      CountOfPackagings: this.countOfPackagings?.value,
      IsTotalQuantity: JSON.parse(this.isTotalQuantity?.value),
    } as SinglePackagingModel;
  }

  private reInitform() {
    this.form.reset({
      useAlternativeProperShippingName: this.model?.UseAlternativeProperShippingName,
      technicalName: this.model?.TechnicalName,
      isEnvironmentallyHazardous: this.model?.IsEnvironmentallyHazardous || false,
      lowerFlashpoint: this.model?.LowerFlashpoint,
      countOfPackagings: this.model?.CountOfPackagings || 1,
      packagingType: this.model?.PackagingType || this.defaultPackageType,
      useAlternativePackagingType: this.model?.UseAlternativePackagingType || false,
      packagingUnCode: this.model?.PackagingUnCode,
      alternativePackagingType: this.model?.AlternativePackagingType,
      isTotalQuantity: this.model?.IsTotalQuantity || false,
      netWeight: this.model?.NetWeightValue || null,
      netVolume: this.model?.NetVolumeValue || null,
      grossWeight: this.model?.GrossWeightValue || null,
      netExplosiveMass: this.model?.NetExplosiveMassValue || null,
    });


    this.properShippingName?.setValue(this.model?.DangerousGood?.DefaultShippingExpression, { emitEvent: false });
    if (!this.model?.IsEnvironmentallyHazardousEnabled || this.model?.IsEnvironmentallyHazardousFromDb) {
      this.isEnvironmentallyHazardous?.disable();
    }
  }

  get properShippingName(): AbstractControl | null {
    return this.form.get('properShippingName');
  }
  get alternativeProperShippingName(): AbstractControl | null {
    return this.form.get('alternativeProperShippingName');
  }
  get useAlternativeProperShippingName(): AbstractControl | null {
    return this.form.get('useAlternativeProperShippingName');
  }
  get technicalName(): AbstractControl | null {
    return this.form.get('technicalName');
  }
  get isEnvironmentallyHazardous(): AbstractControl | null {
    return this.form.get('isEnvironmentallyHazardous');
  }
  get lowerFlashpoint(): AbstractControl | null {
    return this.form.get('lowerFlashpoint');
  }
  get countOfPackagings(): AbstractControl | null {
    return this.form.get('countOfPackagings');
  }
  get packagingType(): AbstractControl | null {
    return this.form.get('packagingType');
  }
  get useAlternativePackagingType(): AbstractControl | null {
    return this.form.get('useAlternativePackagingType');
  }
  get packagingUnCode(): AbstractControl | null {
    return this.form.get('packagingUnCode');
  }
  get alternativePackagingType(): AbstractControl | null {
    return this.form.get('alternativePackagingType');
  }
  get netWeight(): AbstractControl | null {
    return this.form.get('netWeight');
  }
  get netVolume(): AbstractControl | null {
    return this.form.get('netVolume');
  }
  get grossWeight(): AbstractControl | null {
    return this.form.get('grossWeight');
  }
  get netExplosiveMass(): AbstractControl | null {
    return this.form.get('netExplosiveMass');
  }
  get isTotalQuantity(): AbstractControl | null {
    return this.form.get('isTotalQuantity');
  }
}
